import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../home/components/Footer';

export default function DugoLawCaseStudy() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Set page title and meta tags
    document.title = 'Dugo Law Case Study | Family Law Website Design | Bellport Branding';

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    updateMetaTag('description', 'See how Bellport Branding created a professional, compassionate website for Dugo Law, a Connecticut family law firm specializing in divorce, custody, and appeals.');
    updateMetaTag('keywords', 'Dugo Law case study, family law website design, Connecticut attorney website, legal website design, divorce lawyer website');
    
    // Open Graph
    updateMetaTag('og:type', 'article', true);
    updateMetaTag('og:title', 'Dugo Law Case Study | Family Law Website Design', true);
    updateMetaTag('og:description', 'Professional website design for a Connecticut family law firm specializing in divorce and custody matters.', true);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.bellportbranding.com';
    canonical.href = `${siteUrl}/portfolio/dugo-law`;

    // Add Schema.org for case study
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Dugo Law Case Study - Family Law Website Design',
      description: 'A compassionate, professional website for a Connecticut family law firm dedicated to guiding clients through difficult transitions.',
      author: {
        '@type': 'Organization',
        name: 'Bellport Branding'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Bellport Branding',
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/logo.png`
        }
      },
      datePublished: '2026-01-15',
      dateModified: '2026-01-15',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/portfolio/dugo-law`
      }
    };

    const addSchema = (schema: object, id: string) => {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    };

    addSchema(articleSchema, 'article-schema');

    return () => {
      const script = document.getElementById('article-schema');
      if (script) script.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl shadow-sm">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/d7da4bfde00f775727e3be13864eec4a.png" 
                alt="Bellport Branding" 
                className="h-8 w-auto"
              />
            </Link>
            <div className="flex items-center gap-4">
              <a 
                href="tel:+16316407888"
                className="hidden md:flex items-center gap-2 text-neutral-900 hover:text-[#1E3A8A] transition-colors cursor-pointer"
              >
                <span className="w-8 h-8 rounded-full bg-[#1E3A8A] flex items-center justify-center">
                  <i className="ri-phone-fill text-white text-sm"></i>
                </span>
                <span className="font-semibold">(631) 640-7888</span>
              </a>
              <Link
                to="/"
                className="bg-neutral-900 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-neutral-800 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-neutral-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 transition-colors mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Portfolio
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-[#1E3A8A]/10 text-[#1E3A8A] text-xs px-4 py-2 rounded-full font-medium mb-6">
                Website Design & Development
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-neutral-900 leading-tight tracking-tight mb-6">
                Dugo Law
              </h1>
              <p className="text-lg text-neutral-600 leading-relaxed mb-8">
                A compassionate, professional website for a Connecticut family law firm dedicated to guiding clients through life's most difficult transitions with quality, affordable representation.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <a 
                  href="https://www.dugolaw.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-neutral-900 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-neutral-800 transition-all cursor-pointer whitespace-nowrap"
                >
                  Visit Live Site
                  <i className="ri-external-link-line"></i>
                </a>
                <Link 
                  to="/#start-project"
                  className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-700 px-6 py-3 rounded-full text-sm font-medium hover:bg-neutral-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  Start Your Project
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-3xl font-semibold text-neutral-900">2026</p>
                  <p className="text-sm text-neutral-500">Year Completed</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold text-neutral-900">48</p>
                  <p className="text-sm text-neutral-500">Hours to Build</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold text-neutral-900">16+</p>
                  <p className="text-sm text-neutral-500">Pages Designed</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-[#1E3A8A]/5 to-[#1E3A8A]/10 rounded-3xl p-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/d0809f80d042dec55ba24a30b3e22002.jpg"
                  alt="Dugo Law Website Design"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 hidden lg:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <i className="ri-check-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-neutral-900">Mobile Optimized</p>
                    <p className="text-xs text-neutral-500">100% Responsive Design</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-semibold text-neutral-900 mb-6">Project Overview</h2>
              <p className="text-neutral-600 leading-relaxed mb-6">
                Dugo Law was established to bring quality, affordable, and compassionate representation to clients in Connecticut. The firm understands and appreciates that clients are going through some of the most difficult transitions in their lives, whether facing divorce, custody disputes, or complex family law matters.
              </p>
              <p className="text-neutral-600 leading-relaxed mb-6">
                The firm's primary focus is on assisting and guiding clients through the difficulties of their legal disputes while ensuring the best possible resolution. They concentrate their practice on divorce and family matters, including cases involving both significant as well as minimal assets, complex compensation structures, post-judgment modifications and enforcements, alimony terminations, and parenting and custody issues. In addition, they dedicate much of their practice to divorce and family law appeals in both the Connecticut Appellate and Supreme Courts.
              </p>
              <p className="text-neutral-600 leading-relaxed">
                We delivered a comprehensive 16+ page website in just 48 hours that reflects Dugo Law's compassionate approach and extensive expertise. The site positions them as a trusted resource for Connecticut families navigating difficult legal transitions, with a design that balances professionalism with warmth and understanding.
              </p>
            </div>

            <div className="bg-neutral-50 rounded-2xl p-8">
              <h3 className="text-lg font-semibold text-neutral-900 mb-6">Project Details</h3>
              <div className="space-y-6">
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Client</p>
                  <p className="text-neutral-900 font-medium">Dugo Law</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Industry</p>
                  <p className="text-neutral-900 font-medium">Family Law</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Location</p>
                  <p className="text-neutral-900 font-medium">Newtown, Connecticut</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Address</p>
                  <p className="text-neutral-900 font-medium text-sm">12 Queen Street, Suite 4<br />Newtown, CT 06470</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Services Provided</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-xs bg-white px-3 py-1 rounded-full text-neutral-600 border border-neutral-200">Web Design</span>
                    <span className="text-xs bg-white px-3 py-1 rounded-full text-neutral-600 border border-neutral-200">Development</span>
                    <span className="text-xs bg-white px-3 py-1 rounded-full text-neutral-600 border border-neutral-200">SEO</span>
                    <span className="text-xs bg-white px-3 py-1 rounded-full text-neutral-600 border border-neutral-200">Copywriting</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Website</p>
                  <a 
                    href="https://www.dugolaw.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#1E3A8A] font-medium hover:underline cursor-pointer"
                  >
                    www.dugolaw.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-neutral-900 mb-4">Key Features Delivered</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Every element was carefully crafted to serve the firm's business goals and provide an exceptional user experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-[#1E3A8A]/10 flex items-center justify-center mb-6">
                <i className="ri-smartphone-line text-[#1E3A8A] text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Responsive Design</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Fully optimized for all devices, ensuring potential clients can easily browse and contact the firm from any smartphone, tablet, or desktop.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-[#1E3A8A]/10 flex items-center justify-center mb-6">
                <i className="ri-search-eye-line text-[#1E3A8A] text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">SEO Optimized</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Built with search engine optimization best practices to help the firm rank for key family law terms in their Connecticut service area.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-[#1E3A8A]/10 flex items-center justify-center mb-6">
                <i className="ri-phone-line text-[#1E3A8A] text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Click-to-Call</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Prominent phone numbers throughout the site that allow mobile users to call with a single tap, reducing friction for urgent family law matters.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-[#1E3A8A]/10 flex items-center justify-center mb-6">
                <i className="ri-heart-line text-[#1E3A8A] text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Compassionate Messaging</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Carefully crafted copy that balances professionalism with empathy, reflecting the firm's understanding approach to difficult family transitions.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-[#1E3A8A]/10 flex items-center justify-center mb-6">
                <i className="ri-file-list-3-line text-[#1E3A8A] text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Practice Area Pages</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Dedicated pages for divorce, custody, alimony, modifications, and appeals with detailed information to help clients understand their options.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-[#1E3A8A]/10 flex items-center justify-center mb-6">
                <i className="ri-speed-line text-[#1E3A8A] text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Fast Loading</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Optimized performance ensures quick page loads, improving user experience and search engine rankings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Website Screenshots */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-neutral-900 mb-4">Website Showcase</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              A closer look at the design elements and pages we created for Dugo Law.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="group">
              <div className="bg-neutral-100 rounded-2xl p-4 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Family%20law%20firm%20website%20practice%20areas%20page%20design%20showing%20divorce%20custody%20alimony%20sections%20with%20compassionate%20icons%20clean%20grid%20layout%20navy%20blue%20accents%20warm%20professional%20typography%20white%20background%20empathetic%20legal%20website%20aesthetic&width=600&height=450&seq=dugolaw-practice-2b&orientation=landscape"
                  alt="Dugo Law Practice Areas Page"
                  className="w-full h-64 object-cover object-top rounded-xl group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mt-4">Practice Areas</h3>
              <p className="text-sm text-neutral-500">Clear presentation of family law services</p>
            </div>

            <div className="group">
              <div className="bg-neutral-100 rounded-2xl p-4 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Family%20law%20attorney%20profile%20page%20website%20design%20featuring%20compassionate%20lawyer%20headshot%20biography%20credentials%20education%20bar%20admissions%20clean%20layout%20navy%20blue%20and%20warm%20color%20scheme%20modern%20empathetic%20legal%20website%20design&width=600&height=450&seq=dugolaw-attorney-3b&orientation=landscape"
                  alt="Dugo Law Attorney Profile"
                  className="w-full h-64 object-cover object-top rounded-xl group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mt-4">Attorney Profiles</h3>
              <p className="text-sm text-neutral-500">Professional bios that build trust and connection</p>
            </div>

            <div className="group">
              <div className="bg-neutral-100 rounded-2xl p-4 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Family%20law%20firm%20contact%20page%20website%20design%20with%20consultation%20request%20form%20office%20location%20map%20in%20Newtown%20Connecticut%20phone%20number%20email%20address%20business%20hours%20clean%20professional%20layout%20navy%20blue%20accents%20modern%20legal%20website&width=600&height=450&seq=dugolaw-contact-4b&orientation=landscape"
                  alt="Dugo Law Contact Page"
                  className="w-full h-64 object-cover object-top rounded-xl group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mt-4">Contact & Consultation</h3>
              <p className="text-sm text-neutral-500">Easy-to-use contact forms for lead generation</p>
            </div>

            <div className="group">
              <div className="bg-neutral-100 rounded-2xl p-4 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Family%20law%20firm%20website%20mobile%20responsive%20design%20mockup%20showing%20smartphone%20and%20tablet%20views%20of%20compassionate%20legal%20services%20website%20clean%20navigation%20touch%20friendly%20buttons%20professional%20legal%20website%20on%20multiple%20devices&width=600&height=450&seq=dugolaw-mobile-5b&orientation=landscape"
                  alt="Dugo Law Mobile Design"
                  className="w-full h-64 object-cover object-top rounded-xl group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mt-4">Mobile Experience</h3>
              <p className="text-sm text-neutral-500">Seamless experience across all devices</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center mx-auto mb-8">
            <i className="ri-double-quotes-l text-[#1E3A8A] text-3xl"></i>
          </div>
          <blockquote className="text-2xl md:text-3xl font-medium text-neutral-900 leading-relaxed mb-8">
            "Bellport Branding delivered exactly what we needed - a professional website that reflects our compassionate approach to family law. We've seen a significant increase in client inquiries since launch."
          </blockquote>
          <div>
            <p className="font-semibold text-neutral-900">Dugo Law</p>
            <p className="text-sm text-neutral-500">Newtown, Connecticut</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-neutral-900 rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-[#1E3A8A] rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#1E3A8A] rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
            </div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
                Ready for a website like this?
              </h2>
              <p className="text-neutral-400 max-w-xl mx-auto mb-8">
                Let's discuss how we can create a professional website that helps your business grow.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:+16316407888"
                  className="inline-flex items-center justify-center gap-2 bg-white text-neutral-900 px-8 py-4 rounded-full text-sm font-medium hover:bg-neutral-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-fill text-teal-600"></i>
                  Call (631) 640-7888
                </a>
                <Link 
                  to="/"
                  className="inline-flex items-center justify-center gap-2 border border-neutral-700 text-white px-8 py-4 rounded-full text-sm font-medium hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
                >
                  View More Projects
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
